import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, IconButton, Tooltip, ClickAwayListener, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { format, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchData, fetchCollector, setActiveModal, downloadEmail} from '../../slices/invoiceData.slice';
import styles from './DataTable.module.scss';
import FilterIcon from '../../assets/Icons/filter.svg';
import ActiveFilterIcon from '../../assets/Icons/ActiveFilterIcon.svg';
import ClearFilterIcon from '../../assets/Icons/ClearFilter.svg';
import EnvelopeIcon from '../../assets/Icons/sms.svg';
import DocumentIcon from '../../assets/Icons/document.svg';
import EnvelopeTrackingIcon from '../../assets/Icons/sms-tracking.svg';
import EyeIcon from '../../assets/Icons/Eye-icon.svg'
import CustomPagination from '../Pagination/Pagination';
import CustomSearch from '../Search/CustomSearch';
import CustomSortIcon from '../CustomIcons/CustomSortIcon'
import Invoice from '../InvoiceModal';
import { RootState } from '../../store';
import TrackingDialog from '../TrackingDialog/TrackingDialog';
import { sendEmail } from '../../slices/count.slice';
import { startLoading, stopLoading } from '../../slices/loading.slice';
import { SortConfig, columns, getFilterButtons } from '../../utils/DataTableUtils/DataTableUtils';
import CustomButton from '../Button/Button';

const pageSize = 10;

const handleOpenInvoice = (url: string) => {
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const DataTable = () => {
  const dispatch = useAppDispatch();
  const { data, loading, totalResult, error } = useAppSelector(({ invoiceData }: RootState) => invoiceData);
  const { total, overdue, needsReview } = useAppSelector(({ counts }: RootState) => counts);
  const [currentPage, setCurrentPage] = useState(0);
  const [trackingDailogOpen, setTrackingDailogOpen] = useState(false);
  const [selectedTrackingId, setSelectedTrackingId] = useState<string | null>(null);
  const [invoiceType, setInvoiceType] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [tableHeading, setTableHeading] = useState('All Open Invoices');
  const [searchTerm, setsearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [filterColumn, setFilterColumn] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sortBy: '', sortOrder: 'asc' });
  const [isDefaultFilterLoaded, setIsDefaultFilterLoaded] = useState(false);

  const hasFilter = Object.values(filterValues).some((value) => value !== null);

  useEffect(() => {
    dispatch(fetchCollector()).unwrap()
      .then((collector) => {
        setIsDefaultFilterLoaded(true);
        setFilterValues((prev) => ({ ...prev, collector }))
      })
  }, [dispatch]);

  // Fetch API data
  const fetchDataFromApi = useCallback((params: any) => {
    return () => dispatch(fetchData(params))
  }, []);


  useEffect(() => {
    if (!isDefaultFilterLoaded) {
      return;
    }
    const debouncedFetch = debounce(fetchDataFromApi({
      pageNumber: currentPage + 1,
      pageSize: pageSize,
      sortBy: sortConfig.sortBy,
      sortOrder: sortConfig.sortOrder,
      search: searchTerm,
      ...filterValues,
      invoiceType,
    }) as any, 800);

    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
    };
  }, [currentPage, sortConfig, searchTerm, filterValues, invoiceType, dispatch, isDefaultFilterLoaded]);

  const handleSearchChange = (newSearchTerm: string) => {
    setCurrentPage(0);
    setsearchTerm(newSearchTerm)
  };

  const handleSearchClear = () => {
    setCurrentPage(0);
    setsearchTerm('');
  }

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>, columnId: string) => {
    const newFilterValue = event.target.value;

    setCurrentPage(0);
    setFilterValues((prev) => ({ ...prev, [columnId]: newFilterValue }));

    if (columnId === 'collector' && newFilterValue === '') {
      sessionStorage.removeItem('collectorFilter');
    }
  };

  const handleDateFilterChange = (newDate: dayjs.Dayjs | null) => {
    setSelectedDate(newDate);
    setCurrentPage(0);
    if (newDate) {
      const formattedDate = dayjs(newDate).format('YYYY-MM-DD');
      setFilterValues((prev) => ({ ...prev, invoiceDate: formattedDate }));
    } else {
      const updatedFilters = { ...filterValues };
      delete updatedFilters.invoiceDate;
      setFilterValues(updatedFilters);
    }
  };

  const handleFilterButtonClick = (type: number, heading: string) => {
    setInvoiceType(type);
    setTableHeading(heading);
    setCurrentPage(0);
  }

  const onClearFiltersClick = () => {
    setCurrentPage(0);
    setFilterValues({})
  }

  const handleSort = (field: string) => {
    const resetSort = sortConfig.sortBy === field && sortConfig.sortOrder === 'desc';
    if (resetSort) {
      setSortConfig({ sortBy: '', sortOrder: 'asc' });
      return;
    }

    const isAsc = sortConfig.sortBy === field && sortConfig.sortOrder === 'asc';
    setSortConfig({ sortBy: field, sortOrder: isAsc ? 'desc' : 'asc' });
  };

  const handleFilterClick = (columnIndex: number) => {
    setFilterColumn(filterColumn === columnIndex ? null : columnIndex);
  };

  const handleTrackingIconClick = (id: string) => {
    setSelectedTrackingId(id);
    setTrackingDailogOpen(true);
  }

  const handleRowClick = (rowData: any) => {
    dispatch(setActiveModal(rowData));
    setSelectedTrackingId(rowData.id);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    dispatch(setActiveModal(null));
  };

  const onEmailLogged = () => {
    if (!selectedTrackingId) {
      return;
    }

    try {
      dispatch(startLoading());
      dispatch(sendEmail(selectedTrackingId)).unwrap().then(() => {
        if (currentPage === 0) {
          dispatch(fetchData({
            pageNumber: 1,
            pageSize: pageSize,
            sortBy: sortConfig.sortBy,
            sortOrder: sortConfig.sortOrder,
            search: searchTerm,
            ...filterValues,
            invoiceType
          }))
        }
        setCurrentPage(0);
        setTrackingDailogOpen(false);
      });

    } catch (error) {
      toast.error('Failed to log the email');
    } finally {
      dispatch(stopLoading());
    }
  }

  const handleSendEmail = () => {
    setTrackingDailogOpen(true);
    setDialogOpen(false);
  }

  const handleOutstandingBalanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (!isNaN(Number(value)) || (value === '.' && filterValues['outstandingBalance'] === '')) {
      handleFilterChange(event, 'outstandingBalance');
    }
  };

  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <div className={styles.pageHeading}>
        <h1>Invoices Dashboard</h1>
      </div>

      <Box className={styles.topActionBtns}>
        {getFilterButtons(overdue, needsReview, total).map((button) => (
          <Button
            key={button.type}
            className={`${button.className} ${invoiceType === button.value ? styles.active : ''}`}
            variant={invoiceType === button.value ? 'contained' : 'outlined'}
            onClick={() => handleFilterButtonClick(button.value, button.heading)}
          >
            <p><img src={button.icon} alt='Icon' /> {button.label}</p>
            <h4>{button.count} {button.heading}</h4>
          </Button>
        ))}
      </Box>

      <Box className={styles.tableHeading}>
        <Grid container spacing={2}>
          <Grid size={6}>
            <h2>{tableHeading}</h2>
          </Grid>
          <Grid size={6} className={styles.searchANDFilter}>
            <CustomButton onClick={onClearFiltersClick} disabled={!hasFilter}><img src={ClearFilterIcon} />Clear Filter</CustomButton>
            <CustomSearch filterChange={handleSearchChange} searchTerm={searchTerm} handleClear={handleSearchClear} />
          </Grid>
        </Grid>
      </Box>

      <Paper>
        <TableContainer className={styles.tableContainer}>
          {(loading || data === undefined) ? <div className={styles.loaderContainer}>
            <div className={styles.loader}><CircularProgress /></div>
          </div> : null}
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell key={column.id} style={{ width: column.width }}>
                    <TableSortLabel
                      active={sortConfig.sortBy === column.id}
                      direction={sortConfig.sortBy === column.id ? sortConfig.sortOrder : 'asc'}
                      onClick={() => handleSort(column.id)}
                      IconComponent={() => <CustomSortIcon isActive={sortConfig.sortBy === column.id} direction={sortConfig.sortBy === column.id ? sortConfig.sortOrder : 'asc'} />}>
                      {column.label}
                    </TableSortLabel>

                    <IconButton onClick={() => handleFilterClick(index)} size="small">
                      {filterValues[column.id] ? (
                        <img src={ActiveFilterIcon} alt="Active Filter Icon" />
                      ) : (
                        <img src={FilterIcon} alt="Filter Icon" />
                      )}
                    </IconButton>

                    {filterColumn === index && (
                      <ClickAwayListener onClickAway={() => setFilterColumn(null)}>
                        <div className={styles.filterInput}>
                          {column.id === 'invoiceDate' ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={selectedDate}
                                onChange={handleDateFilterChange}
                                open={isDatePickerOpen}
                                onClose={() => setIsDatePickerOpen(false)}
                                slotProps={{
                                  textField: {
                                    variant: 'outlined',
                                    style: { backgroundColor: '#fff' },
                                    fullWidth: true,
                                    autoComplete: 'off',
                                    onClick: () => setIsDatePickerOpen(true),
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          ) :  column.id === 'outstandingBalance' ? (
                            <TextField
                              placeholder={`${column.label}`}
                              variant="outlined"
                              value={filterValues['outstandingBalance'] || ''}
                              onChange={handleOutstandingBalanceChange}
                              autoFocus
                              style={{ backgroundColor: '#fff' }}
                              autoComplete="off"
                            />
                          ) : (
                              <TextField
                                placeholder={`${column.label}`}
                                variant="outlined"
                                value={filterValues[column.id] || ''}
                                onChange={(event: any) => handleFilterChange(event, column.id)}
                                autoFocus
                                style={{ backgroundColor: '#fff' }}
                                autoComplete="off"
                              />
                            )}
                        </div>
                      </ClickAwayListener>
                    )}
                  </TableCell>
                ))}
                <TableCell style={{ width: '11%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data || data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                    {data === undefined ? '' : ' No data found'}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, rowIndex) => (
                  <TableRow 
                    key={rowIndex}
                    className={`${item.botStatus === 'Error' ? styles.errorRow : ''} ${styles.rowHover}`}>
                    {columns.map((column, colIndex) => (
                      <TableCell key={column.id} className={colIndex === 2 ? styles.tooltipCell : ''} onClick={() => {
                        if (colIndex < 6) {
                          handleRowClick(item)
                        }
                      }}>
                        {column.id === 'clientName' ? (
                          <Tooltip title={item[column.id]}>
                            <span>{item[column.id]}</span>
                          </Tooltip>
                        ) : column.id === 'invoiceDate' ? (
                          format(parseISO(item[column.id]), 'MM/dd/yyyy')
                        ) : column.id === 'outstandingBalance' && item[column.id] ? (
                          `$${parseFloat(item[column.id]).toLocaleString()}`
                        ) : (
                          item[column.id]
                        )
                        }
                      </TableCell>
                    ))}
                    <TableCell>
                      <Box sx={{ flexGrow: 1 }}>
                        <Grid container spacing={2}>
                        <Grid size={3} className={styles.trackAction}>
                            <Tooltip title="View More Information">
                            <IconButton onClick={() => handleRowClick(item)}>
                               <img src={EyeIcon} alt="Icon" className={styles.cursorPointer} />
                              </IconButton>
                            
                            </Tooltip>
                          </Grid>
                          <Grid size={3} className={styles.trackAction}>
                            {item.sharePointInvoicePath ? (
                              <Tooltip title="Open Invoice">
                              <IconButton onClick={() => handleOpenInvoice(item.sharePointInvoicePath)}>
                                 <img src={DocumentIcon} alt="Icon" />
                              </IconButton>
                              </Tooltip>
                            ): (
                              <Tooltip title="Open Invoice">
                                <img src = { DocumentIcon } alt = "Icon" className = {styles.actionIcons} />
                                </Tooltip>
                            )}
                          </Grid>
                          <Grid size={3} className={styles.trackAction}>
                            {item.sharePointEmailPath ? (
                              <Tooltip title="Open Email">
                              <IconButton onClick={() => dispatch(downloadEmail(item.id))}>
                                <img src={EnvelopeIcon} alt="Icon" />
                              </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Open Email">
                              <img src={EnvelopeIcon} alt="Icon" className={styles.actionIcons} />
                              </Tooltip>
                            )}
                          </Grid>
                          <Grid size={3} className={styles.trackAction}>
                          <Tooltip title="Log Email Send">
                            <IconButton onClick={() => handleTrackingIconClick(item.id)}>
                              <img src={EnvelopeTrackingIcon} alt="Icon" />
                            </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {dialogOpen && <Invoice open={dialogOpen} handleClose={handleClose} handleSendEmail={handleSendEmail} handleOpenInvoice={handleOpenInvoice}/>}
        <TrackingDialog
          open={trackingDailogOpen}
          onClose={() => setTrackingDailogOpen(false)}
          onEmailLogged={onEmailLogged}
          clientName={selectedTrackingId ? data?.find((item) => item.id === selectedTrackingId)?.clientName || '' : ''}
        />
      </Paper>

      {totalResult > pageSize && (
        <CustomPagination
          pageCount={Math.ceil(totalResult / pageSize)}
          currentPage={currentPage}
          onPageChange={(selectedItem) => setCurrentPage(selectedItem)}
        />
      )}
    </div>
  );
};

export default DataTable;
